import { Row } from 'react-table';

import { summarizeRowsAbove } from '../constants';

/**
 * Apply reducing logic to a given row and return reduced value.
 */
type ReducerFn<ReducedValue> = (
    acc: ReducedValue,
    row: Row<TrqkGapChannelAudio>,
) => ReducedValue;

/**
 * Combine an accumulated value with the result for the current row or sub-row.
 */
type CombineFn<ReducedValue> = (
    acc: ReducedValue,
    result: ReducedValue,
) => ReducedValue;

interface ReduceFooterValueInput<ReducedValue> {
    /**
     * Function to use when combining an accumulated value with the result for the current row
     * or sub-row.
     */
    combineFn: CombineFn<ReducedValue>;
    /**
     * Value passed as the second argument to Array.reduce().
     */
    initialValue: ReducedValue;
    /**
     * Function passed as the first argument to Array.reduce().
     */
    reducerFn: ReducerFn<ReducedValue>;
    /**
     * Array of rows to reduce
     */
    rows: Row<TrqkGapChannelAudio>[];
}

/**
 * Reduces a footer value from an array of rows.
 * @remarks This function is confusing to read. To help understand how it works, see the
 * sumFooterValue function, which uses a similar logic but uses summing.
 * @param input - see {@link ReduceFooterValueInput}
 * @returns - reduced value to be displayed in footer
 */
export function reduceFooterValue<ReducedValue>({
    combineFn,
    initialValue,
    reducerFn,
    rows,
}: ReduceFooterValueInput<ReducedValue>): ReducedValue {
    return rows.reduce((acc: ReducedValue, row: Row<TrqkGapChannelAudio>) => {
        const { canExpand, isExpanded } = row;

        // If the row should not be summarized, just reduce the row itself. In this case, the
        // aggregated value will be used.
        if (row.subRows.length <= summarizeRowsAbove) {
            return reducerFn(acc, row);
        }

        // When the row can be expanded, but is not expanded, we need to reduce all the sub-rows
        if (canExpand && !isExpanded) {
            return combineFn(acc, row.subRows.reduce(reducerFn, acc));
        }

        return acc;
    }, initialValue);
}
